import { Link } from "react-router-dom";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { formatDateTime } from "../../lib/format";
import { useCan, useForexAdmin } from "./api";
import { BASE, Money, Panel, QueryState, Stat, Unavail } from "./parts";

const RED = "border-red-500/30 bg-red-500/[0.06]";
const AMBER = "border-amber-500/30 bg-amber-500/[0.06]";

// Kind then purpose (018): both purposes shown, never merged.
const Counts = ({ c }) => (
  <>
    {c.regular + c.copyTrading}
    <span className="block text-xs text-muted-foreground">{c.regular} regular · {c.copyTrading} copy trading</span>
  </>
);

function MoneyRow({ label, m }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      {["balance", "credit", "equity", "floatingPnl", "realizedPnl"].map((k) => (
        <Stat key={k} label={`${label} ${k.replace("Pnl", " P&L")}`}>
          <Money value={m[k]} />
        </Stat>
      ))}
    </div>
  );
}

// `to` is null when the role does not open the page it points at.
function Alert({ to, label, value, bad }) {
  const stat = (
    <Stat label={label} tone={bad ? RED : undefined}>
      {value}
    </Stat>
  );
  return to ? <Link to={to}>{stat}</Link> : stat;
}

// Overview, health and sweeper load as three calls, so a slow mirror or queue
// never holds up the rest. Overview and health need reports.read, the sweeper
// settlements.read (025); a panel the role does not open is left out.
export default function Overview() {
  const can = useCan();
  const reports = can("reports.read");
  const settlements = can("settlements.read");
  const overview = useForexAdmin(reports ? "/reports/admin/overview" : null);
  const health = useForexAdmin(reports ? "/reports/admin/health" : null);
  const sweeper = useForexAdmin(settlements ? "/settlements/admin/sweeper" : null);
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;

  return (
    <QueueLayout
      eyebrow="Forex Admin"
      title="Forex"
      accent="overview"
      description="Totals across the service, what needs attention, and whether each part answers."
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      {!reports && !settlements && (
        <p className="text-sm text-muted-foreground">
          Your forex role opens none of the overview panels. Use the pages in the sidebar.
        </p>
      )}
      {reports && <QueryState q={overview}>
        {(o) => (
          <div className="space-y-8">
            <Panel title="Needs attention">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Alert
                  to={can("transfers.read") ? `${BASE}/transfers?tab=stranded` : null}
                  label="Stranded transfers"
                  value={o.strandedTransfers}
                  bad={o.strandedTransfers > 0}
                />
                <Alert
                  to={settlements ? `${BASE}/settlements?tab=charges&state=UNKNOWN` : null}
                  label="Unknown charges"
                  value={o.unknownCharges}
                  bad={o.unknownCharges > 0}
                />
                {/* v1.28.0 (042): releases that failed 3+ times in a row; operators are emailed. */}
                <Alert
                  to={settlements ? `${BASE}/settlements?tab=charges&state=AWAITING_RELEASE` : null}
                  label="Failing releases"
                  value={o.failingReleases}
                  bad={o.failingReleases > 0}
                />
                {/* null = the queue did not answer: unknown, not fine. */}
                <Stat label="Sweeper" tone={o.sweeperLate ? RED : o.sweeperLate == null ? AMBER : undefined}>
                  {o.sweeperLate == null ? "Unknown" : o.sweeperLate ? "Late" : "On time"}
                </Stat>
              </div>
            </Panel>

            <Panel title="Counts">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <Stat label="Users">{o.users}</Stat>
                <Stat label="Total live accounts"><Counts c={o.accountCounts.real} /></Stat>
                <Stat label="Demo accounts"><Counts c={o.accountCounts.demo} /></Stat>
                <Stat label="Live masters">{o.liveMasters}</Stat>
                <Stat label="Standing connections">{o.standingConnections}</Stat>
              </div>
            </Panel>

            <Panel title="Money">
              {o.money ? (
                <div className="space-y-3">
                  <MoneyRow label="Real" m={o.money.real} />
                  <MoneyRow label="Demo" m={o.money.demo} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  <Unavail /> The trading figures are unavailable right now. Counts above are unaffected.
                </p>
              )}
            </Panel>
          </div>
        )}
      </QueryState>}

      <div className="grid gap-8 lg:grid-cols-2">
        {reports && <Panel title="Health">
          <QueryState q={health}>
            {(h) => (
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(h).map(([name, c]) => (
                  <Stat
                    key={name}
                    label={name}
                    tone={c.status === "DOWN" ? RED : c.tookMs > 2000 ? AMBER : undefined}
                  >
                    {c.status}
                    <span className="ml-2 text-xs text-muted-foreground">{c.tookMs} ms</span>
                    {/* null = the check failed or the mirror holds no deal: unavailable, not zero (018). */}
                    {name === "mirror" && (
                      <div className="text-xs text-muted-foreground">
                        last deal {c.lagSeconds == null ? <Unavail /> : `${Math.round(c.lagSeconds / 60)} min ago`}
                      </div>
                    )}
                    {name === "engine" && (
                      <div className="text-xs text-muted-foreground">
                        last progress {c.lastProgressAt == null ? <Unavail /> : formatDateTime(c.lastProgressAt)}
                      </div>
                    )}
                  </Stat>
                ))}
              </div>
            )}
          </QueryState>
        </Panel>}

        {settlements && <Panel title="Sweeper">
          {/* 016: grey on any error, not a red failure. */}
          {sweeper.error ? (
            <Stat label="Status">
              <span className="text-muted-foreground">Status unknown</span>
              <span className="block text-xs text-muted-foreground">{sweeper.error.message}</span>
            </Stat>
          ) : (
          <QueryState q={sweeper}>
            {(s) => (
              <div className="grid grid-cols-2 gap-3">
                <Stat
                  label="Last run"
                  tone={s.late ? RED : s.lastRun?.outcome === "FAILED" ? AMBER : undefined}
                >
                  {s.lastRun ? s.lastRun.outcome : "Never"}
                  {s.late && <span className="ml-2 text-xs text-red-400">late</span>}
                  {s.lastRun?.failedReason && (
                    <div className="break-words text-xs text-muted-foreground">
                      {s.lastRun.failedReason}
                    </div>
                  )}
                </Stat>
                <Stat label="Started">
                  <span className="text-sm">{formatDateTime(s.lastRun?.startedAt)}</span>
                </Stat>
                <Stat label="Finished">
                  <span className="text-sm">{formatDateTime(s.lastRun?.finishedAt)}</span>
                </Stat>
                <Stat label="Next run">
                  <span className="text-sm">{formatDateTime(s.nextRunAt)}</span>
                </Stat>
                <Stat label="Every">
                  {s.everyMs == null ? "—" : `${s.everyMs / 1000}s`}
                </Stat>
              </div>
            )}
          </QueryState>
          )}
        </Panel>}
      </div>
    </QueueLayout>
  );
}
